<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SectionCard from 'dashboard/components-next/section-card/SectionCard.vue';
import DragDropOverlay from '@/dashboard/routes/dashboard/settings/knowledgebase/components/DragDropOverlay.vue';
import { useFileUpload } from '@/dashboard/routes/dashboard/settings/knowledgebase/useFileUpload';
import { useBulkFileUpload } from '@/dashboard/routes/dashboard/settings/knowledgebase/useBulkFileUpload';
import SpacesAPI from '@/dashboard/api/spaces';
import EventsAPI from '@/dashboard/api/planning/events';
import SpaceDescription from './components/SpaceDescription.vue';
import SpaceCapacity from './components/SpaceCapacity.vue';
import SpaceTierSelect from './components/SpaceTierSelect.vue';
import CapacityField from './components/CapacityField.vue';
import SpaceAvailability from './components/SpaceAvailability.vue';
import SpaceClosedPeriods from './components/SpaceClosedPeriods.vue';
import SpaceDocuments from './components/SpaceDocuments.vue';
import SpaceGallery from './components/SpaceGallery.vue';
import SpaceAttributes from './components/SpaceAttributes.vue';
import FloorplanLayoutsAPI from '@/dashboard/api/floorplanLayouts';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const loading = ref(true);
const space = ref(null);
const editingName = ref(false);
const tempName = ref('');
const saving = ref(false);
const structureSaving = ref(false);
const showDeleteModal = ref(false);
const showDeleteRecordModal = ref(false);
const recordToDelete = ref(null);
const documentRecords = ref([]);
const galleryRecords = ref([]);
const events = ref([]);
const loadingEvents = ref(false);
const attributeDefinitions = ref({});
const selectedParent = ref(null);
const selectedVenue = ref(null);

const spaceId = computed(() => route.params.spaceId);

let suggestedPriceRefreshSent = false;

const refreshSuggestedPriceOnLeave = () => {
  if (suggestedPriceRefreshSent || !spaceId.value) return;
  suggestedPriceRefreshSent = true;
  SpacesAPI.refreshSuggestedPrice(spaceId.value).catch(() => {});
};

onBeforeRouteLeave(() => {
  refreshSuggestedPriceOnLeave();
});

onBeforeUnmount(() => {
  refreshSuggestedPriceOnLeave();
});

const allSpaces = computed(() => store.getters['spaces/getSpaces'] || []);
const venues = computed(() => store.getters['venues/getVenues'] || []);

const hasChildren = computed(() => (space.value?.children || []).length > 0);

const parentSpaceOptions = computed(() =>
  allSpaces.value
    .filter(s => !s.parent_id && s.id !== spaceId.value)
    .map(s => ({ id: s.id, name: s.name, icon: 'i-lucide-orbit' }))
);

const venueOptions = computed(() =>
  venues.value.map(v => ({
    id: v.id,
    name: v.name,
    icon: 'i-lucide-building-2',
  }))
);

const syncSelectedParent = () => {
  if (!space.value?.parent_id) {
    selectedParent.value = null;
    return;
  }

  const parent =
    space.value.parent ||
    allSpaces.value.find(s => s.id === space.value.parent_id);
  selectedParent.value = parent ? { id: parent.id, name: parent.name } : null;
};

const syncSelectedVenue = () => {
  if (!space.value?.venue_id) {
    selectedVenue.value = null;
    return;
  }

  const venue = venues.value.find(v => v.id === space.value.venue_id);
  selectedVenue.value = venue ? { id: venue.id, name: venue.name } : null;
};

const fileInputRef = ref(null);
const uploadingToGallery = ref(false);

const { uploadFile, attachFile } = useFileUpload();
const { addFilesToQueue, processQueue, isDragOver } = useBulkFileUpload();

const knowledgebaseRecords = computed(() => {
  const sorted = [...documentRecords.value];
  sorted.sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });
  return sorted;
});

const sortedGalleryRecords = computed(() => {
  const sorted = [...galleryRecords.value];
  sorted.sort((a, b) => {
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });
  return sorted;
});

const featuredCoverUrl = computed(() => {
  if (!space.value) return null;
  if (space.value.media_url) return space.value.media_url;

  const featuredId = space.value.featured_record_id;
  if (featuredId == null || featuredId === '') return null;

  const record = galleryRecords.value.find(
    r => String(r.id) === String(featuredId)
  );
  return record?.file?.url || null;
});

const fetchSpace = async () => {
  try {
    loading.value = true;
    space.value = await store.dispatch('spaces/show', spaceId.value);

    // Fetch both document and gallery records
    const promises = [];
    if (space.value?.knowledgebase_id) {
      promises.push(
        store.dispatch('records/get', space.value.knowledgebase_id).then(() => {
          documentRecords.value = [...store.getters['records/getRecords']];
        })
      );
    }
    if (space.value?.gallery_knowledgebase_id) {
      promises.push(
        store
          .dispatch('records/get', space.value.gallery_knowledgebase_id)
          .then(() => {
            galleryRecords.value = [...store.getters['records/getRecords']];
          })
      );
    }
    await Promise.all(promises);
    syncSelectedParent();
    syncSelectedVenue();
  } catch (error) {
    router.push({ name: 'settings_spaces' });
  } finally {
    loading.value = false;
  }
};

const fetchEvents = async () => {
  if (!spaceId.value) return;

  try {
    loadingEvents.value = true;
    const response = await EventsAPI.getAll({
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
      space_id: spaceId.value,
      limit: 10,
    });
    events.value = response.data || [];
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch events:', error);
    events.value = [];
  } finally {
    loadingEvents.value = false;
  }
};

const fetchAttributeDefinitions = async () => {
  try {
    const response = await SpacesAPI.getAttributeDefinitions();
    attributeDefinitions.value = response.data || {};
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch attribute definitions:', error);
  }
};

onMounted(async () => {
  await Promise.all([
    store.dispatch('spaces/get'),
    store.dispatch('venues/get'),
  ]);
  await fetchSpace();
  await fetchEvents();
  await fetchAttributeDefinitions();
  await fetchLayouts();
});

const handleBack = () => {
  router.push({ name: 'settings_spaces_index' });
};

const startEditingName = () => {
  tempName.value = space.value?.name || '';
  editingName.value = true;
};

const saveSpaceName = async () => {
  if (!tempName.value.trim()) return;
  try {
    saving.value = true;
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      name: tempName.value,
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    }
    editingName.value = false;
  } finally {
    saving.value = false;
  }
};

const cancelEditingName = () => {
  editingName.value = false;
  tempName.value = '';
};

const saveDescription = async newDescription => {
  try {
    saving.value = true;
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      description: newDescription,
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    }
  } finally {
    saving.value = false;
  }
};

const updateAvailability = async (field, value) => {
  try {
    saving.value = true;
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      [field]: value,
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    }
  } finally {
    saving.value = false;
  }
};

const updateSpaceField = async (field, cleanValue) => {
  try {
    saving.value = true;
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      [field]: cleanValue,
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    } else {
      await fetchSpace();
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error updating space field:', error);
    await fetchSpace();
  } finally {
    saving.value = false;
  }
};

const updateCapacity = async (field, value) => {
  if (field === 'tier') {
    await updateSpaceField('tier', value || null);
    return;
  }

  let cleanValue = value;
  if (value === 'n/a') {
    cleanValue = 'n/a';
  } else if (
    value === '' ||
    value === null ||
    value === undefined ||
    Number.isNaN(Number(value))
  ) {
    cleanValue = null;
  } else {
    cleanValue = Number(value);
  }

  await updateSpaceField(field, cleanValue);
};

const persistParentId = async parentId => {
  try {
    structureSaving.value = true;
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      parent_id: parentId,
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    }
    syncSelectedParent();
  } catch (error) {
    syncSelectedParent();
    useAlert(
      error?.response?.data?.errors?.parent_id ||
        error?.response?.data?.errors?.base ||
        error?.message ||
        t('SETTINGS.SPACES.SAVE_ERROR')
    );
  } finally {
    structureSaving.value = false;
  }
};

const onParentChange = async parent => {
  const newParentId = parent?.id || null;
  if (newParentId === (space.value?.parent_id || null)) return;

  selectedParent.value = parent;
  await persistParentId(newParentId);
};

const clearParent = async () => {
  if (!space.value?.parent_id) return;
  selectedParent.value = null;
  await persistParentId(null);
};

const persistVenueId = async venueId => {
  try {
    structureSaving.value = true;
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      venue_id: venueId,
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    }
    syncSelectedVenue();
  } catch (error) {
    syncSelectedVenue();
    useAlert(
      error?.response?.data?.message ||
        error?.message ||
        t('SETTINGS.SPACES.VENUE_ASSIGN_ERROR')
    );
  } finally {
    structureSaving.value = false;
  }
};

const onVenueChange = async venue => {
  const newVenueId = venue?.id || null;
  if (newVenueId === (space.value?.venue_id || null)) return;

  selectedVenue.value = venue;
  await persistVenueId(newVenueId);
};

const clearVenue = async () => {
  if (!space.value?.venue_id) return;
  selectedVenue.value = null;
  await persistVenueId(null);
};

const navigateToChildSpace = childId => {
  router.push({ name: 'settings_spaces_show', params: { spaceId: childId } });
};

const updateSchedule = async data => {
  try {
    saving.value = true;
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      working_hours_enabled: data.working_hours_enabled,
      schedule: data.schedule,
      timezone: data.timezone,
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    }
    await store.dispatch('planningResources/get');
    useAlert(t('SETTINGS.SPACES.SCHEDULE.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.base ||
        error?.message ||
        t('SETTINGS.SPACES.SCHEDULE.UPDATE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const updateClosedPeriods = async closedPeriods => {
  try {
    saving.value = true;
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      working_hours_enabled: space.value.working_hours_enabled,
      timezone: space.value.timezone,
      schedule: {
        days: space.value.schedule?.days || [],
        closed_periods: closedPeriods,
      },
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    }
    await store.dispatch('planningResources/get');
    useAlert(t('SETTINGS.SPACES.CLOSED_PERIODS.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.errors?.base ||
        error?.message ||
        t('SETTINGS.SPACES.CLOSED_PERIODS.UPDATE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const updateAttribute = async (key, value) => {
  try {
    saving.value = true;
    const updatedAttributes = {
      ...(space.value.custom_attributes || {}),
      [key]: value,
    };
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      custom_attributes: updatedAttributes,
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    }
  } finally {
    saving.value = false;
  }
};

const removeAttribute = async key => {
  try {
    saving.value = true;
    const current = { ...(space.value.custom_attributes || {}) };
    delete current[key];
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      custom_attributes: current,
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    }
  } finally {
    saving.value = false;
  }
};

const handleDelete = () => {
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  try {
    await store.dispatch('spaces/delete', spaceId.value);
    router.push({ name: 'settings_spaces_index' });
  } catch (error) {
    // Error handled by store
  }
};

const cancelDelete = () => {
  showDeleteModal.value = false;
};

const handleDeleteRecord = (record, isGallery = false) => {
  recordToDelete.value = { record, isGallery };
  showDeleteRecordModal.value = true;
};

const handleSetFeatured = async recordId => {
  try {
    saving.value = true;
    const updatedSpace = await store.dispatch('spaces/update', {
      id: spaceId.value,
      featured_record_id: recordId,
    });
    if (updatedSpace) {
      space.value = updatedSpace;
    }
  } finally {
    saving.value = false;
  }
};

const confirmDeleteRecord = async () => {
  if (!recordToDelete.value) return;

  const { record, isGallery } = recordToDelete.value;
  const knowledgebaseId = isGallery
    ? space.value.gallery_knowledgebase_id
    : space.value.knowledgebase_id;
  const wasFeatured =
    isGallery &&
    space.value?.featured_record_id != null &&
    String(space.value.featured_record_id) === String(record.id);

  try {
    await store.dispatch('records/delete', {
      knowledgebaseId,
      recordId: record.id,
    });

    // Refresh the appropriate record list
    await store.dispatch('records/get', knowledgebaseId);
    if (isGallery) {
      galleryRecords.value = [...store.getters['records/getRecords']];
      if (wasFeatured) {
        const updatedSpace = await store.dispatch('spaces/update', {
          id: spaceId.value,
          featured_record_id: null,
        });
        if (updatedSpace) {
          space.value = updatedSpace;
        }
      }
    } else {
      documentRecords.value = [...store.getters['records/getRecords']];
    }
  } catch (error) {
    // Error handled by store
  } finally {
    showDeleteRecordModal.value = false;
    recordToDelete.value = null;
  }
};

const cancelDeleteRecord = () => {
  showDeleteRecordModal.value = false;
  recordToDelete.value = null;
};

const navigateToKnowledgebase = () => {
  if (space.value?.knowledgebase_id) {
    router.push({
      name: 'settings_knowledgebases_content',
      params: { knowledgebaseId: space.value.knowledgebase_id },
    });
  }
};

const navigateToGallery = () => {
  if (space.value?.gallery_knowledgebase_id) {
    router.push({
      name: 'settings_knowledgebases_content',
      params: { knowledgebaseId: space.value.gallery_knowledgebase_id },
    });
  }
};

const navigateToFloorplan = () => {
  router.push({
    name: 'settings_spaces_floorplan',
    params: { spaceId: spaceId.value },
  });
};

// ── Floorplan Layouts ──

const layouts = ref([]);
const loadingLayouts = ref(false);
const newLayoutName = ref('');
const showNewLayoutInput = ref(false);
const creatingLayout = ref(false);

const fetchLayouts = async () => {
  loadingLayouts.value = true;
  try {
    const { data } = await FloorplanLayoutsAPI.list(spaceId.value);
    layouts.value = data;
  } catch {
    layouts.value = [];
  } finally {
    loadingLayouts.value = false;
  }
};

const createLayout = async () => {
  const name = newLayoutName.value.trim();
  if (!name) return;
  creatingLayout.value = true;
  try {
    await FloorplanLayoutsAPI.create(spaceId.value, { name });
    newLayoutName.value = '';
    showNewLayoutInput.value = false;
    await fetchLayouts();
  } catch {
    useAlert(t('FLOORPLAN.LAYOUTS.CREATE_ERROR'));
  } finally {
    creatingLayout.value = false;
  }
};

const deleteLayout = async layoutId => {
  try {
    await FloorplanLayoutsAPI.destroy(spaceId.value, layoutId);
    await fetchLayouts();
  } catch {
    useAlert(t('FLOORPLAN.LAYOUTS.DELETE_ERROR'));
  }
};

const openLayoutEditor = layoutId => {
  router.push({
    name: 'settings_spaces_floorplan_layout',
    params: { spaceId: spaceId.value, layoutId },
  });
};

// File upload handlers
const handleUploadClick = (isGallery = false) => {
  uploadingToGallery.value = isGallery;
  if (fileInputRef.value) {
    fileInputRef.value.click();
  } else {
    // eslint-disable-next-line no-console
    console.error('File input ref not found');
  }
};

const onFileChange = async event => {
  const files = Array.from(event.target.files);
  if (files.length === 0) return;

  const targetKnowledgebaseId = uploadingToGallery.value
    ? space.value.gallery_knowledgebase_id
    : space.value.knowledgebase_id;

  if (!targetKnowledgebaseId) return;

  if (files.length === 1) {
    const file = files[0];
    const upload = await uploadFile(file, targetKnowledgebaseId);
    await attachFile({
      file,
      upload,
      knowledgebaseId: targetKnowledgebaseId,
    });
    // Refresh records
    await store.dispatch('records/get', targetKnowledgebaseId);
    if (uploadingToGallery.value) {
      galleryRecords.value = [...store.getters['records/getRecords']];
    } else {
      documentRecords.value = [...store.getters['records/getRecords']];
    }
  } else {
    addFilesToQueue(files, targetKnowledgebaseId);
    setTimeout(() => {
      processQueue();
    }, 100);
  }

  event.target.value = '';
};

// Drag and drop handlers
const handleDragEnter = e => {
  e.preventDefault();
  e.stopPropagation();
  isDragOver.value = true;
};

const handleDragOver = e => {
  e.preventDefault();
  e.stopPropagation();
};

const handleDragLeave = e => {
  e.preventDefault();
  e.stopPropagation();
  if (e.target.classList.contains('drag-drop-container')) {
    isDragOver.value = false;
  }
};

const handleDrop = async e => {
  e.preventDefault();
  e.stopPropagation();
  isDragOver.value = false;

  const files = Array.from(e.dataTransfer.files);
  if (files.length === 0) return;

  // Check if files are images
  const imageFiles = files.filter(file => file.type.startsWith('image/'));
  const otherFiles = files.filter(file => !file.type.startsWith('image/'));

  // Upload images to gallery knowledgebase
  if (imageFiles.length > 0) {
    const galleryKnowledgebaseId = space.value?.gallery_knowledgebase_id;
    if (galleryKnowledgebaseId) {
      addFilesToQueue(imageFiles, galleryKnowledgebaseId);
    }
  }

  // Upload other files to documents knowledgebase
  if (otherFiles.length > 0) {
    const documentsKnowledgebaseId = space.value?.knowledgebase_id;
    if (documentsKnowledgebaseId) {
      addFilesToQueue(otherFiles, documentsKnowledgebaseId);
    }
  }

  if (imageFiles.length > 0 || otherFiles.length > 0) {
    setTimeout(async () => {
      await processQueue();
      // Refresh both record lists after upload completes
      if (imageFiles.length > 0 && space.value?.gallery_knowledgebase_id) {
        await store.dispatch(
          'records/get',
          space.value.gallery_knowledgebase_id
        );
        galleryRecords.value = [...store.getters['records/getRecords']];
      }
      if (otherFiles.length > 0 && space.value?.knowledgebase_id) {
        await store.dispatch('records/get', space.value.knowledgebase_id);
        documentRecords.value = [...store.getters['records/getRecords']];
      }
    }, 100);
  }
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-white dark:bg-slate-900">
    <div
      v-if="!loading && space"
      class="flex flex-col h-full bg-n-surface-1 overflow-hidden drag-drop-container"
      @dragenter="handleDragEnter"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <DragDropOverlay :is-drag-over="isDragOver" />
      <!-- Top Header -->
      <div
        class="py-5 flex items-center justify-between bg-n-surface-1 shrink-0"
      >
        <div
          class="max-w-7xl w-full mx-auto px-6 flex items-center justify-between"
        >
          <div class="flex items-center gap-4">
            <button
              class="flex items-center px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 rounded-md hover:bg-purple-100 transition-colors"
              @click="handleBack"
            >
              <Icon icon="i-lucide-undo-2" class="size-4 mr-2" />
              {{ t('SETTINGS.SPACES.ALL_SPACES') }}
            </button>
            <div v-if="editingName" class="flex items-center gap-2">
              <input
                v-model="tempName"
                type="text"
                class="reset-base no-margin text-xl font-bold text-slate-900 dark:text-white bg-transparent border-b-2 border-purple-500 focus:outline-none min-w-[300px] px-2 py-1"
                autofocus
                @keyup.enter="saveSpaceName"
                @keyup.escape="cancelEditingName"
              />
              <button
                class="p-1 text-purple-600 hover:text-purple-700"
                :disabled="saving"
                @click="saveSpaceName"
              >
                <Icon icon="i-lucide-check" class="size-5" />
              </button>
              <button
                class="p-1 text-slate-400 hover:text-slate-600"
                @click="cancelEditingName"
              >
                <Icon icon="i-lucide-x" class="size-5" />
              </button>
            </div>
            <h1
              v-else
              class="text-xl font-bold text-slate-900 dark:text-white cursor-pointer hover:text-purple-600 transition-colors"
              :title="t('SETTINGS.SPACES.CLICK_TO_EDIT')"
              @click="startEditingName"
            >
              {{ space.name }}
            </h1>
          </div>

          <div class="flex items-center gap-3">
            <button
              class="p-2 text-slate-400 hover:text-white hover:bg-red-600 rounded-lg transition-colors flex h-10 w-10 justify-center items-center"
              @click="handleDelete"
            >
              <Icon icon="i-lucide-trash-2" class="size-5" />
            </button>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="flex-1 overflow-hidden">
        <div class="h-full overflow-y-auto">
          <div class="max-w-7xl mx-auto px-6 py-8 space-y-8">
            <!-- Description Section -->
            <SectionCard
              :show-drag-handle="false"
              :title="t('SETTINGS.SPACES.DESCRIPTION.TITLE')"
              icon="i-lucide-file-text"
            >
              <SpaceDescription
                :description="space.description"
                :saving="saving"
                @save="saveDescription"
              />
            </SectionCard>

            <!-- Structure Section -->
            <SectionCard
              :show-drag-handle="false"
              :title="t('SETTINGS.SPACES.STRUCTURE.TITLE')"
              icon="i-lucide-layers"
            >
              <p class="text-sm text-slate-500 dark:text-slate-400 mb-4">
                {{ t('SETTINGS.SPACES.STRUCTURE.DESCRIPTION') }}
              </p>
              <div class="flex flex-wrap items-end gap-x-8 gap-y-4">
                <div class="flex flex-col gap-2 min-w-[12rem]">
                  <label
                    class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ t('SETTINGS.SPACES.STRUCTURE.VENUE_LABEL') }}
                  </label>
                  <div class="flex items-center gap-2">
                    <SingleSelect
                      v-model="selectedVenue"
                      :options="venueOptions"
                      :placeholder="t('SETTINGS.SPACES.VENUES.NO_VENUE')"
                      :search-placeholder="
                        t('SETTINGS.SPACES.VENUES.NAME_PLACEHOLDER')
                      "
                      :disabled="structureSaving"
                      @update:model-value="onVenueChange"
                    />
                    <Button
                      v-if="selectedVenue"
                      v-tooltip.top="t('SETTINGS.SPACES.STRUCTURE.CLEAR_VENUE')"
                      icon="i-lucide-x"
                      slate
                      faded
                      sm
                      :disabled="structureSaving"
                      @click="clearVenue"
                    />
                  </div>
                </div>

                <div
                  v-if="!hasChildren"
                  class="flex flex-col gap-2 min-w-[12rem]"
                >
                  <label
                    class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ t('SETTINGS.SPACES.STRUCTURE.PARENT_LABEL') }}
                  </label>
                  <div class="flex items-center gap-2">
                    <SingleSelect
                      v-model="selectedParent"
                      :options="parentSpaceOptions"
                      :placeholder="t('SETTINGS.SPACES.NO_PARENT')"
                      :search-placeholder="t('SETTINGS.SPACES.SEARCH_PARENT')"
                      :disabled="structureSaving"
                      @update:model-value="onParentChange"
                    />
                    <Button
                      v-if="selectedParent"
                      v-tooltip.top="
                        t('SETTINGS.SPACES.STRUCTURE.CLEAR_PARENT')
                      "
                      icon="i-lucide-x"
                      slate
                      faded
                      sm
                      :disabled="structureSaving"
                      @click="clearParent"
                    />
                  </div>
                </div>

                <div class="flex flex-col gap-2 min-w-[12rem] flex-1">
                  <span
                    class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >
                    {{ t('SETTINGS.SPACES.STRUCTURE.CHILDREN_LABEL') }}
                  </span>
                  <div
                    class="flex flex-wrap items-center gap-x-3 gap-y-1 min-h-8"
                  >
                    <span
                      v-if="(space.children || []).length === 0"
                      class="text-sm text-slate-500 dark:text-slate-400"
                    >
                      {{ t('SETTINGS.SPACES.STRUCTURE.NO_CHILDREN') }}
                    </span>
                    <template v-else>
                      <button
                        v-for="child in space.children"
                        :key="child.id"
                        type="button"
                        class="inline-flex items-center gap-1.5 text-sm text-woot-600 hover:text-woot-700 transition-colors"
                        @click="navigateToChildSpace(child.id)"
                      >
                        <Icon icon="i-lucide-orbit" class="size-4" />
                        {{ child.name }}
                      </button>
                    </template>
                  </div>
                </div>
              </div>
            </SectionCard>

            <!-- Capacity Section -->
            <SectionCard
              :show-drag-handle="false"
              :title="t('SETTINGS.SPACES.CAPACITY.TITLE')"
              icon="i-lucide-users"
            >
              <div class="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                <SpaceTierSelect
                  :label="t('SETTINGS.SPACES.TIER_LABEL')"
                  :model-value="space.tier || null"
                  @update:model-value="updateCapacity('tier', $event)"
                />
                <CapacityField
                  field-key="square_meters"
                  :label="t('SETTINGS.SPACES.SQUARE_METERS')"
                  :value="space.square_meters"
                  suffix="m²"
                  step="0.01"
                  @update="value => updateCapacity('square_meters', value)"
                />
              </div>
              <SpaceCapacity
                :theater="space.theater"
                :classroom="space.classroom"
                :standing="space.standing"
                :banquet="space.banquet"
                :boardroom="space.boardroom"
                :u-shape="space.u_shape"
                :concert="space.concert"
                :cocktail="space.cocktail"
                :max-capacity-seated="space.max_capacity_seated"
                :max-capacity-seated-furn="space.max_capacity_seated_furn"
                :max-capacity-standing="space.max_capacity_standing"
                :saving="saving"
                @update="updateCapacity"
              />
            </SectionCard>

            <!-- Attributes Section -->
            <SectionCard
              :show-drag-handle="false"
              :title="t('SETTINGS.SPACES.ATTRIBUTES.TITLE')"
              icon="i-lucide-tags"
            >
              <SpaceAttributes
                :custom-attributes="space.custom_attributes || {}"
                :attribute-definitions="attributeDefinitions"
                :saving="saving"
                @update="updateAttribute"
                @remove="removeAttribute"
              />
            </SectionCard>

            <!-- Two Column Layout -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <!-- Documents Column -->
              <SectionCard
                :show-drag-handle="false"
                :title="t('SETTINGS.SPACES.DOCUMENTS.TITLE')"
                icon="i-lucide-file-text"
              >
                <SpaceDocuments
                  :records="knowledgebaseRecords"
                  :knowledgebase-id="space.knowledgebase_id"
                  @upload="() => handleUploadClick(false)"
                  @delete="record => handleDeleteRecord(record, false)"
                />
              </SectionCard>

              <!-- Gallery Column -->
              <SectionCard
                :show-drag-handle="false"
                :title="t('SETTINGS.SPACES.GALLERY.TITLE')"
                icon="i-lucide-images"
              >
                <div
                  v-if="featuredCoverUrl"
                  class="mb-4 overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700 aspect-[16/7] bg-slate-100 dark:bg-slate-900"
                >
                  <img
                    :src="featuredCoverUrl"
                    :alt="t('SETTINGS.SPACES.GALLERY.IMAGE_ALT')"
                    class="w-full h-full object-cover"
                  />
                </div>
                <div
                  v-else-if="sortedGalleryRecords.length > 0"
                  class="mb-4 flex items-center justify-center rounded-lg border border-dashed border-slate-200 dark:border-slate-700 aspect-[16/7] bg-slate-50 dark:bg-slate-900/50"
                >
                  <p class="text-sm text-slate-500">
                    {{ t('SETTINGS.SPACES.GALLERY.EMPTY_COVER') }}
                  </p>
                </div>
                <SpaceGallery
                  :records="sortedGalleryRecords"
                  :knowledgebase-id="space.gallery_knowledgebase_id"
                  :featured-record-id="space.featured_record_id"
                  @upload="() => handleUploadClick(true)"
                  @delete="record => handleDeleteRecord(record, true)"
                  @set-featured="handleSetFeatured"
                />
              </SectionCard>
            </div>

            <!-- Floorplan Layouts Section -->
            <SectionCard
              :show-drag-handle="false"
              :title="t('FLOORPLAN.LAYOUTS.TITLE')"
              icon="i-lucide-layers"
            >
              <div class="space-y-3">
                <div
                  v-if="loadingLayouts"
                  class="pt-4 text-center text-sm text-slate-400"
                >
                  {{ t('FLOORPLAN.LAYOUTS.LOADING') }}
                </div>
                <div
                  v-else-if="layouts.length === 0 && !showNewLayoutInput"
                  class="pt-4 text-center text-sm text-slate-400"
                >
                  {{ t('FLOORPLAN.LAYOUTS.EMPTY') }}
                </div>
                <div
                  v-for="layout in layouts"
                  :key="layout.id"
                  class="flex items-center justify-between p-3 rounded-lg border border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <div class="flex items-center gap-3">
                    <Icon
                      icon="i-lucide-layout-dashboard"
                      class="size-4 text-slate-400"
                    />
                    <span
                      class="text-sm font-medium text-slate-700 dark:text-slate-200"
                      >{{ layout.name }}</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      class="p-1.5 text-woot-600 hover:bg-woot-25 rounded transition-colors"
                      :title="t('FLOORPLAN.LAYOUTS.OPEN_EDITOR')"
                      @click="openLayoutEditor(layout.id)"
                    >
                      <Icon icon="i-lucide-pencil" class="size-4" />
                    </button>
                    <button
                      class="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      :title="t('FLOORPLAN.LAYOUTS.DELETE')"
                      @click="deleteLayout(layout.id)"
                    >
                      <Icon icon="i-lucide-trash-2" class="size-4" />
                    </button>
                  </div>
                </div>

                <div v-if="showNewLayoutInput" class="flex items-center gap-2">
                  <input
                    v-model="newLayoutName"
                    type="text"
                    class="reset-base no-margin flex-1 text-sm border border-slate-200 dark:border-slate-600 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-woot-500"
                    :placeholder="t('FLOORPLAN.LAYOUTS.NAME_PLACEHOLDER')"
                    autofocus
                    @keyup.enter="createLayout"
                    @keyup.escape="showNewLayoutInput = false"
                  />
                  <button
                    class="p-2 text-woot-600 hover:text-woot-700"
                    :disabled="creatingLayout"
                    @click="createLayout"
                  >
                    <Icon icon="i-lucide-check" class="size-5" />
                  </button>
                  <button
                    class="p-2 text-slate-400 hover:text-slate-600"
                    @click="showNewLayoutInput = false"
                  >
                    <Icon icon="i-lucide-x" class="size-5" />
                  </button>
                </div>

                <button
                  v-if="!showNewLayoutInput"
                  class="flex items-center gap-2 text-sm text-woot-600 hover:text-woot-700 transition-colors"
                  @click="showNewLayoutInput = true"
                >
                  <Icon icon="i-lucide-plus" class="size-4" />
                  {{ t('FLOORPLAN.LAYOUTS.ADD') }}
                </button>
              </div>
            </SectionCard>

            <!-- Schedule Section -->
            <SectionCard
              :show-drag-handle="false"
              :title="t('SETTINGS.SPACES.SCHEDULE.TITLE')"
              icon="i-lucide-calendar-clock"
            >
              <SpaceAvailability
                :active="space.active"
                :schedule="space.schedule"
                :timezone="space.timezone || ''"
                :working-hours-enabled="space.working_hours_enabled ?? false"
                :saving="saving"
                @update-availability="updateAvailability"
                @update-schedule="updateSchedule"
              />
            </SectionCard>

            <SectionCard
              :show-drag-handle="false"
              :title="t('SETTINGS.SPACES.CLOSED_PERIODS.TITLE')"
              icon="i-lucide-calendar-off"
            >
              <SpaceClosedPeriods
                :schedule="space.schedule"
                :saving="saving"
                @update-closed-periods="updateClosedPeriods"
              />
            </SectionCard>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
        @click.self="cancelDelete"
      >
        <div
          class="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 transform transition-all"
        >
          <div
            class="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full mb-4"
          >
            <Icon
              icon="i-lucide-alert-triangle"
              class="size-6 text-red-600 display-block"
            />
          </div>
          <h3
            class="text-lg font-semibold text-center text-slate-900 dark:text-white mb-2"
          >
            {{ t('SETTINGS.SPACES.DELETE_CONFIRM_TITLE') }}
          </h3>
          <p
            class="text-sm text-center text-slate-500 dark:text-slate-400 mb-6"
          >
            {{ t('SETTINGS.SPACES.DELETE_CONFIRM_MESSAGE') }}
          </p>
          <div class="grid grid-cols-2 gap-3">
            <button
              class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600"
              @click="cancelDelete"
            >
              {{ t('SETTINGS.SPACES.DELETE_CONFIRM_NO') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 shadow-sm"
              @click="confirmDelete"
            >
              {{ t('SETTINGS.SPACES.DELETE_CONFIRM_YES') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Record Confirmation Modal -->
      <div
        v-if="showDeleteRecordModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
        @click.self="cancelDeleteRecord"
      >
        <div
          class="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 transform transition-all"
        >
          <div
            class="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full mb-4"
          >
            <Icon
              icon="i-lucide-alert-triangle"
              class="size-6 text-red-600 display-block"
            />
          </div>
          <h3
            class="text-lg font-semibold text-center text-slate-900 dark:text-white mb-2"
          >
            {{ t('SETTINGS.SPACES.DELETE_RECORD_CONFIRM_TITLE') }}
          </h3>
          <p
            class="text-sm text-center text-slate-500 dark:text-slate-400 mb-6"
          >
            {{ t('SETTINGS.SPACES.DELETE_RECORD_CONFIRM_MESSAGE') }}
          </p>
          <div class="grid grid-cols-2 gap-3">
            <button
              class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-600"
              @click="cancelDeleteRecord"
            >
              {{ t('SETTINGS.SPACES.DELETE_CONFIRM_NO') }}
            </button>
            <button
              class="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              @click="confirmDeleteRecord"
            >
              {{ t('SETTINGS.SPACES.DELETE_CONFIRM_YES') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-else
      class="flex items-center justify-center h-full bg-white dark:bg-slate-900"
    >
      <div class="flex flex-col items-center">
        <div
          class="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mb-3"
        />
        <p class="text-sm text-slate-500">{{ t('SETTINGS.SPACES.LOADING') }}</p>
      </div>
    </div>

    <!-- Hidden file input for uploads -->
    <input
      ref="fileInputRef"
      type="file"
      multiple
      class="hidden"
      @change="onFileChange"
    />
  </div>
</template>
